import React, {
	memo,
	useState,
	useEffect,
	useRef,
	useMemo,
	useCallback,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useTheme, makeStyles } from '@material-ui/styles'
import { Theme, ButtonBase } from '@material-ui/core'
import { StoryStateValue } from '../../reducer'
// Froala Editor
// Require Editor JS files.
import 'froala-editor/js/froala_editor.pkgd.min.js'
import 'froala-editor/js/plugins/align.min.js'
import 'froala-editor/js/plugins/lists.min.js'
import 'froala-editor/js/plugins/link.min.js'
import 'froala-editor/js/plugins/image.min.js'
import 'froala-editor/js/plugins/video.min.js'
import 'froala-editor/js/plugins/quick_insert.min.js'
import 'froala-editor/js/plugins/char_counter.min.js'
// Require Editor CSS files.
import 'froala-editor/css/froala_style.min.css'
import 'froala-editor/css/froala_editor.pkgd.min.css'
import 'froala-editor/css/plugins/image.min.css'
import 'froala-editor/css/plugins/video.min.css'
import 'froala-editor/css/plugins/quick_insert.min.css'
import 'froala-editor/js/plugins/char_counter.min.js'
// Require Font Awesome.
import 'font-awesome/css/font-awesome.css'
import FroalaEditor from 'react-froala-wysiwyg'
import FroalaEditorView from 'react-froala-wysiwyg/FroalaEditorView'
import { FROALA_KEY } from 'utils'
import { EArticleActionTypes } from 'models'

import styles from '../styles'
const useStyles = makeStyles(styles)

const EditorView: React.FunctionComponent<any> = props => {
	const {
		isDownSm,
		preview,
		done,
		containerRef,
		body,
		companyStoryDispatch,
	} = props
	const [isFocused, setIsFocused] = useState(false)
	const theme: Theme = useTheme()
	const classes = useStyles()
	const [maxHeight, setMaxHeight] = useState(0)
	// const [{ body }, dispatch] = useStoryState()
	const handleChange = (value: string) => {
		companyStoryDispatch({
			type: EArticleActionTypes.setBody,
			body: value,
		})
	}
	const { t } = useTranslation()
	let editor = useRef()
	const config = useMemo(
		() => ({
			events: {
				'edit.on': function () {
					// @ts-ignore
					if (editor && !editor.current) editor.current = this
				},
				'paste.afterCleanup': function (html: string) {
					let div = document.createElement('div')
					div.innerHTML = html
					return div.innerText
				},
				initialized: function () {
					// @ts-ignore
					if (editor && !editor.current) editor.current = this
					// @ts-ignore
					// this.events.focus()
				},
				focus: function () {
					setIsFocused(true)
				},
				blur: function () {
					setIsFocused(false)
				},
			},
			videoUpload: false,
			imageUploadURL: false,
			placeholderText: t('Describe your story about your office ...'),
			charCounterCount: true,
			quickInsertEnabled: !isDownSm,
			toolbarInline: !isDownSm,
			imageInsertButtons: ['imageBack', '|', 'imageByURL', 'imageUpload'],
			toolbarButtonsXS: [
				'bold',
				'italic',
				'underline',
				'insertLink',
				'insertImage',
				'insertVideo',
			],
			pluginsEnabled: [
				'align',
				'charCounter',
				'link',
				'lists',
				'quickInsert',
			],
			key: FROALA_KEY,
			attribution: false,
			heightMax: isDownSm && !preview ? maxHeight : undefined,
			pastePlain: true,
		}),
		[
			editor,
			isDownSm,
			maxHeight,
			preview,
			// s3UploadHash.bucket,
			// s3UploadHash.keyStart,
			// s3UploadHash.params,
			// s3UploadHash.region,
			t,
		],
	)
	const onContainerClick = useCallback(
		frWrapper => () => {
			// @ts-ignore
			if (isFocused) return null
			// @ts-ignore
			editor.current.events.focus(true)
			// @ts-ignore
			// editor.current.selection.setAtEnd(frWrapper)
			// @ts-ignore
			editor.current.selection.restore()
		},
		[editor, isFocused],
	)

	useEffect(() => {
		if (!editor || !editor.current) return undefined
		// @ts-ignore
		const frWrapper = editor.current.$el.get(0)
		if (frWrapper.parentNode) {
			// @ts-ignore
			// editor.current.selection.setAtEnd(frWrapper)
			// @ts-ignore
			// editor.current.selection.restore()
			frWrapper.parentNode.addEventListener(
				'click',
				onContainerClick(frWrapper),
			)
		}
	}, [isFocused, onContainerClick])

	useEffect(() => {
		if (isDownSm) {
			setMaxHeight(window.innerHeight - 48 * 3)
		} else {
			const target: any = containerRef.current
			if (target) {
				setMaxHeight(target.getBoundingClientRect().height - 38)
			}
		}
	}, [containerRef, isDownSm, maxHeight])

	const handlePublishArticle = useCallback(() => {
		done()
	}, [done])

	return preview ? (
		<div
			className={classes.preview}
			style={!body ? { color: theme.palette.grey[300] } : {}}
		>
			<FroalaEditorView
				model={body || t('Describe your story about your office...')}
			/>
		</div>
	) : (
		(!!maxHeight && (
			<>
				<FroalaEditor
					tag="textarea"
					config={config}
					model={body}
					onModelChange={handleChange}
				/>
				{isDownSm && (
					<div className={classes.buttonsContainer}>
						<ButtonBase
							className={classes.saveButton}
							onClick={handlePublishArticle}
						>
							{t('Done')}
						</ButtonBase>
					</div>
				)}
			</>
		)) ||
			null
	)
}

const Editor = memo((props: any) => (
	<StoryStateValue keys={['body', 'title', 'companyStoryDispatch']}>
		<EditorView {...props} />
	</StoryStateValue>
))

export default memo(Editor)
